import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Row } from 'react-bootstrap';
import { useHistory, useLocation, matchPath } from 'react-router-dom';
import NavLink from '../../../../../NavLink';
import ClickableComponent from '../../../../../ClickableComponent';
import CheckPermissions from '../../../../../CheckPermissions';

import './style/menu.css';

/**
 * A component that renders a user menu
 *
 */
export default function UserMenu({ items }) {
  const history = useHistory();
  const [activeMenu, setActiveMenu] = useState();
  const location = useLocation();

  const menuEntries = useMemo(() => Object.entries(items), [items]);
  const hasSubmenu = useMemo(() => menuEntries.some(([, menu]) => !!menu.subMenu), [menuEntries]);

  const activateWrapperMenu = useCallback((menuItems, currentLocation) => {
    for (const [key, menu] of menuItems) {
      if (!menu.subMenu) {
        if (
          matchPath(currentLocation, {
            path: menu.route.path,
            exact: menu.route.exact,
          })
        ) {
          setActiveMenu(key);
          return;
        }
      } else {
        for (const item of menu.subMenu) {
          if (
            matchPath(currentLocation, {
              path: item.route.path,
              exact: item.route.exact,
            })
          ) {
            setActiveMenu(key);
            return;
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!hasSubmenu) {
      // If the menu doesn't have a submenu, the NavLink will take care of everything
      return;
    }

    activateWrapperMenu(menuEntries, location.pathname);
  }, [activateWrapperMenu, hasSubmenu, location.pathname, menuEntries]);

  const [mainMenu, subMenu] = useMemo(() => {
    const main = [];
    const sub = [];

    for (const [key, menu] of menuEntries) {
      if (!menu.subMenu) {
        // Render a simple menu
        main.push(
          <CheckPermissions key={`main-${key}`} routes={[menu.route]}>
            <NavLink to={menu.route} activeClassName="is-active">
              {menu.label}
              {menu.component && <menu.component />}
            </NavLink>
          </CheckPermissions>
        );

        continue;
      }

      main.push(
        <CheckPermissions key={`main-${key}`} routes={menu.subMenu.map((item) => item.route)}>
          <ClickableComponent
            className={activeMenu === key ? 'is-active' : ''}
            onClick={() => history.push(menu.subMenu?.[0]?.route?.path ?? '')}
          >
            {menu.label}
          </ClickableComponent>
        </CheckPermissions>
      );

      sub.push(
        <React.Fragment key={`collapse-${key}`}>
          {activeMenu === key && (
            <Row noGutters className="submenu-container">
              {menu.subMenu.map((item) => (
                <CheckPermissions key={item.route.path} routes={[item.route]}>
                  <NavLink to={item.route} activeClassName="is-submenu-active">
                    {item.label}
                    {item.component && <item.component />}
                  </NavLink>
                </CheckPermissions>
              ))}
            </Row>
          )}
        </React.Fragment>
      );
    }

    return [main, sub];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, activeMenu, menuEntries]);

  return (
    <>
      {mainMenu}
      {subMenu}
    </>
  );
}

UserMenu.propTypes = {
  items: PropTypes.shape().isRequired,
};
